/**
@format
@flow
*/
import React, {
  createContext, useContext, useEffect, useState,
} from 'react';
import { createPortal } from 'react-dom';
import ActionTypes from '@ziynx/action-types';
import styled from 'styled-components';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { NotificationBanner as BannerNotification } from '@krispy/notifications/web';

type Props = {
  children: any,
}

type NotificationType = {
  variant: any,
  content: any,
}

type DispatchAction = {
  type: string,
  payload?: Notification
} => void;

type ContextType = {
  notificationService: NotificationType,
  notificationDispatch: DispatchAction,
};

const StyledBannerWrapper = styled.div`
  position: fixed;
  top: 0;
  z-index: 996;
  width: 100%;
  flex: 1;
`;

export const NotificationContext: React$Context<ContextType | typeof undefined> = createContext();

export const NotificationManager = (props: Props): React$Node => {
  const { children } = props;
  const notificationService: NotificationType = useSelector(
    ({ notifications }) => notifications, shallowEqual,
  );
  const notificationDispatch: DispatchAction = useDispatch();
  const [userClosed, setUserClosed] = useState(false);

  // FEED INTO CONTEXT PROVIDER
  const notificationData = { notificationService, notificationDispatch };

  /**
   * CHECK FOR NOTIFICATION OBJECT
   * TO FIRE REMOVE-DELAY FOR WHEN
   * BANNER AUTO TIMES OUT
   */
  useEffect(() => {
    let timeout;
    if (!!Object.keys(notificationService).length && !userClosed) {
      timeout = setTimeout(() => {
        notificationDispatch({
          type: ActionTypes.notifications.remove,
        });
      }, 6000);
    }

    if (timeout) {
      return () => clearTimeout(timeout);
    }
  }, [notificationService, userClosed]);

  const NotificationChild = ({ notification }: { notification: NotificationType }) => (
    <StyledBannerWrapper>
      <BannerNotification
        animated
        showCloseIcon
        variant={notification?.variant}
        timeout={5000}
        onClose={() => {
          setTimeout(() => {
            setUserClosed(true);
            notificationDispatch({
              type: ActionTypes.notifications.remove,
            });
          }, 1000);
        }}
      >
        {notification?.content}
      </BannerNotification>

    </StyledBannerWrapper>

  );

  return (
    <NotificationContext.Provider value={notificationData}>
      {children}
      {!!Object.keys(notificationService).length && createPortal(<NotificationChild
        notification={notificationService}
      />, document.body)}
    </NotificationContext.Provider>
  );
};

const useNotification = (): any => useContext(NotificationContext);

export default useNotification;
